import { httpClient } from '@shared/api';
import type { WidgetConfig, WidgetConfigPatch, WidgetSnippet } from '../model/types';

/**
 * Кабинет-эндпоинты для управления виджетом.
 *
 * Соответствует richat_backend/api/routers/widgets.py.
 *
 * Префикс /widgets/{channel_id}/ — три endpoint:
 *   GET    config   — текущие настройки + publishable_key
 *   PATCH  config   — частичное обновление
 *   GET    snippet  — готовая <script>-строка для копирования
 *
 * Все эндпоинты требуют авторизации (кабинет) — на бэке проверяется
 * что текущий user владеет ассистентом этого channel.
 */
export const widgetApi = {
  /** GET /widgets/{channel_id}/config */
  async getConfig(channelId: string): Promise<WidgetConfig> {
    const response = await httpClient.get<WidgetConfig>(`/widgets/${channelId}/config`);
    return response.data;
  },

  /**
   * PATCH /widgets/{channel_id}/config — partial update.
   *
   * Возвращает обновлённый полный конфиг. brand_contrast и
   * *_custom_contrast пересчитываются бэком автоматически по brand_color
   * / bg_custom / bot_bg_custom (формула — YIQ для определения
   * светлый/тёмный фон, выбор чёрного или белого текста).
   *
   * allowed_origins передаётся целиком (replace, не merge) — если нужно
   * добавить домен, фронт сам делает [...prev, newDomain].
   *
   * Сброс bg_custom / bot_bg_custom — через reset_bg_custom: true.
   * Просто bg_custom: null не сработает (Pydantic не отличит «не прислали»
   * от «прислали null» в partial-update без discriminator).
   */
  async patchConfig(channelId: string, payload: WidgetConfigPatch): Promise<WidgetConfig> {
    const response = await httpClient.patch<WidgetConfig>(`/widgets/${channelId}/config`, payload);
    return response.data;
  },

  /** GET /widgets/{channel_id}/snippet */
  /** V2-4 (экран 7): фото ассистента — квадрат 256×256, кладём на CDN. */
  async uploadAvatar(channelId: string, file: File): Promise<WidgetConfig> {
    const form = new FormData();
    form.append('file', file);
    const response = await httpClient.post<WidgetConfig>(`/widgets/${channelId}/avatar`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60_000,
    });
    return response.data;
  },

  async getSnippet(channelId: string): Promise<WidgetSnippet> {
    const response = await httpClient.get<WidgetSnippet>(`/widgets/${channelId}/snippet`);
    return response.data;
  },
};
